import jwtDecode from "jwt-decode";
import queryString from "query-string";
import { Environment, environmentSettings } from "./environments";

const startUri = `${window.location.origin}/sign-popup-start.html`;
const redirectUri = `${window.location.origin}/sign-popup-callback.html`;

export class SignError {
  constructor(public message: string) {}
}

export interface SignManagerOptions {
  // The tokens are assumed to have been validated outside
  getAccessToken: () => Promise<string>;
  getIdToken: () => Promise<string>;
  environment: Environment;
}

interface SignStartResponse {
  requestId: string;
  collectId: string;
}

type SignStartQuery = (userId: string, clientId: string) => Promise<SignStartResponse>;

type CollectQuery<T> = (collectId: string) => Promise<T>;

// tslint:disable-next-line:max-classes-per-file
export class SignManager {
  private rejectActiveSignRequest: (() => void) | null = null;

  constructor(public signOptions: SignManagerOptions) {}

  public async sign(text: string, data: string, preOpenedPopup?: Window) {
    if (this.rejectActiveSignRequest !== null) {
      this.rejectActiveSignRequest();
    }

    const popup = preOpenedPopup || this.openPopup();
    this.rejectActiveSignRequest = () => popup.close();

    try {
      const signQuery = this.createSignStartQuery(text, data);
      return await this.signNoErrorHandling(signQuery, this.collect, popup);
    } catch (ex) {
      console.log("Sign aborted due to exception: ", ex);
      throw ex;
    } finally {
      popup.close();
    }
  }

  public async signMany(text: string, dataObjects: string[], preOpenedPopup?: Window) {
    if (this.rejectActiveSignRequest !== null) {
      this.rejectActiveSignRequest();
    }

    const popup = preOpenedPopup || this.openPopup();
    this.rejectActiveSignRequest = () => popup.close();

    try {
      const signQuery = this.createSignManyStartQuery(text, dataObjects);
      return await this.signNoErrorHandling(signQuery, this.collectMany, popup);
    } catch (ex) {
      console.log("Sign aborted due to exception: ", ex);
      throw ex;
    } finally {
      popup.close();
    }
  }

  public async signJson(text: string, obj: any, preOpenedPopup?: Window) {
    if (this.rejectActiveSignRequest !== null) {
      this.rejectActiveSignRequest();
    }

    const popup = preOpenedPopup || this.openPopup();
    this.rejectActiveSignRequest = () => popup.close();

    try {
      const signQuery = this.createSignJsonStartQuery(text, JSON.stringify(obj));
      return await this.signNoErrorHandling(signQuery, this.collect, popup);
    } catch (ex) {
      console.log("Sign aborted due to exception: ", ex);
      throw ex;
    } finally {
      popup.close();
    }
  }

  public async signManyJson(text: string, objs: any[], preOpenedPopup?: Window) {
    if (this.rejectActiveSignRequest !== null) {
      this.rejectActiveSignRequest();
    }

    const popup = preOpenedPopup || this.openPopup();
    this.rejectActiveSignRequest = () => popup.close();

    try {
      const signQuery = this.createSignManyJsonStartQuery(text, objs.map(x => JSON.stringify(x)));
      return await this.signNoErrorHandling(signQuery, this.collectMany, popup);
    } catch (ex) {
      console.log("Sign aborted due to exception: ", ex);
      throw ex;
    } finally {
      popup.close();
    }
  }

  public openPopup() {
    const requestedWidth = 500;
    const requestedHeight = 600;

    // https://stackoverflow.com/questions/4068373/center-a-popup-window-on-screen
    const dualScreenLeft =
      window.screenLeft !== undefined ? window.screenLeft : (window.screen as any).left;
    const dualScreenTop =
      window.screenTop !== undefined ? window.screenTop : (window.screen as any).top;

    const width = window.innerWidth
      ? window.innerWidth
      : document.documentElement.clientWidth
      ? document.documentElement.clientWidth
      : window.screen.width;
    const height = window.innerHeight
      ? window.innerHeight
      : document.documentElement.clientHeight
      ? document.documentElement.clientHeight
      : window.screen.height;

    const left = width / 2 - requestedWidth / 2 + dualScreenLeft;
    const top = height / 2 - requestedHeight / 2 + dualScreenTop;
    const popupFeatures = `location=no,toolbar=no,width=${requestedWidth},height=${requestedHeight},left=${left},top=${top}`;

    const popupTarget = "SigningWindow";
    const signPopup = window.open(startUri, popupTarget, popupFeatures)!;
    signPopup.focus();
    return signPopup;
  }

  private createSignStartQuery(text: string, data: string) {
    return async (userId: string, clientId: string): Promise<SignStartResponse> => {
      const resp = await this.gql({
        query: `
        mutation StartSign($userId: String!, $clientId: String!, $text: String!, $data: String!) { 
          signResponse: signStart(text: $text, data: $data, clientId: $clientId, userId: $userId) { 
            requestId, 
            collectId 
          } 
        }`,
        variables: {
          text,
          data,
          userId,
          clientId
        }
      });

      return {
        requestId: resp.data.signResponse.requestId,
        collectId: resp.data.signResponse.collectId
      };
    };
  }

  private createSignManyStartQuery(text: string, dataObjects: string[]) {
    return async (userId: string, clientId: string) => {
      const resp = await this.gql({
        query: `
        mutation StartSignMany($userId: String!, $clientId: String!, $text: String!, $dataObjects: [String!]!) { 
          signResponse: signManyStart(text: $text, dataObjects: $dataObjects, clientId: $clientId, userId: $userId) { 
            requestId, 
            collectId 
          } 
        }`,
        variables: {
          text,
          dataObjects,
          userId,
          clientId
        }
      });

      return {
        requestId: resp.data.signManyStart.requestId,
        collectId: resp.data.signManyStart.collectId
      };
    };
  }

  private createSignJsonStartQuery(text: string, json: string) {
    return async (userId: string, clientId: string) => {
      const resp = await this.gql({
        query: `
        mutation StartSign($userId: String!, $clientId: String!, $text: String!, $json: String!) { 
          signJsonStart(text: $text, json: $json, clientId: $clientId, userId: $userId) { 
            requestId, 
            collectId 
          } 
        }`,
        variables: {
          text,
          json,
          userId,
          clientId
        }
      });

      return {
        requestId: resp.data.signJsonStart.requestId,
        collectId: resp.data.signJsonStart.collectId
      };
    };
  }

  private createSignManyJsonStartQuery(text: string, jsonObjects: string[]) {
    return async (userId: string, clientId: string) => {
      const resp = await this.gql({
        query: `
        mutation StartSign($userId: String!, $clientId: String!, $text: String!, $jsonObjects: [String!]!) { 
          signManyJsonStart(text: $text, jsonObjects: $jsonObjects, clientId: $clientId, userId: $userId) { 
            requestId, 
            collectId 
          } 
        }`,
        variables: {
          text,
          jsonObjects,
          userId,
          clientId
        }
      });

      return {
        requestId: resp.data.signManyJsonStart.requestId,
        collectId: resp.data.signManyJsonStart.collectId
      };
    };
  }

  private collect = async (collectId: string): Promise<string> => {
    const collectResp = await this.gql({
      query: `mutation CollectSign { signCollect(collectId: "${collectId}") { signature } }`
    });
    return collectResp.data.signCollect.signature;
  };

  private collectMany = async (collectId: string): Promise<string[]> => {
    const collectResp = await this.gql({
      query: `mutation CollectSignMany { signManyCollect(collectId: "${collectId}") { signatures } }`
    });
    return collectResp.data.signManyCollect.signatures;
  };

  private async signNoErrorHandling<T>(
    signQuery: SignStartQuery,
    collectQuery: CollectQuery<T>,
    popup: Window
  ) {
    const serializedIdToken = await this.signOptions.getIdToken();
    const idToken: any = jwtDecode(serializedIdToken);
    const userId = idToken.sub;
    const clientId = idToken.aud;
    const loa = idToken.acr || "";

    const signResp = await signQuery(userId, clientId);

    await this.signInPopup(popup, signResp.requestId, loa);

    const minDelay = new Promise<void>(resolve => setTimeout(resolve, 500));

    const signature = await collectQuery(signResp.collectId);

    // Make sure we get a feeling that something is signed
    await minDelay;

    return signature;
  }

  private async signInPopup(signPopup: Window, requestId: string, loa: string) {
    let signResolve: () => void | undefined;
    let signReject: (err: string) => void | undefined;
    const signCompletionPromise = new Promise<void>((resolve, reject) => {
      signResolve = resolve;
      signReject = reject;
    });
    (window as any).popupCallback = () => signResolve();

    const qs = queryString.stringify({
      redirectUri,
      requestId,
      loa
    });
    signPopup.window.location.assign(
      `${environmentSettings[this.signOptions.environment].url}/sign?` + qs
    );

    // Monitor popup closed
    const temp = window.setInterval(() => {
      if (signPopup.closed) {
        signReject("Popup closed.");
      }
    }, 250);

    try {
      await signCompletionPromise;
    } finally {
      window.clearInterval(temp);
    }
  }

  private async gql(document: { query: string; variables?: any }) {
    const apiUrl = environmentSettings[this.signOptions.environment].apiUrl; 

    return fetch(`${apiUrl}/graphql`, {
      headers: {
        "Content-Type": "application/json",
        authorization: "Bearer " + (await this.signOptions.getAccessToken())
      },
      method: "POST",
      body: JSON.stringify(document)
    }).then(async x => {
      const parsedResponse = await x.json();
      if (parsedResponse.errors) {
        throw new SignError(parsedResponse.errors[0]);
      }

      return parsedResponse;
    });
  }
}
